import React, { useState } from 'react'
import ReactSelect from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { isEmpty, isObject } from 'lodash'

import { MultiSelect } from './MultiSelect'
import {
  selectStyles,
  selectTheme,
  Input,
  formatOptionWithDescription,
  MENU_PORTAL_TARGET,
} from '../helpers/selectHelpers'

const handleNumberOnlyInput = (e) => {
  if (e.nativeEvent.code === 'KeyE' || e.nativeEvent.code === 'Minus') {
    e.preventDefault()
  }
}

// react-select's default filter matches typed digits anywhere in "<label> <value>",
// so a 50,000 preset survives a search for "5000" and keeps the focus that Enter
// commits. Exact-match while typing; the full list is still browsable when empty.
const exactNumberMatch = (option, rawInput) => {
  const input = rawInput.trim()
  return isEmpty(input) || String(option.value) === input
}

/**
 * @import import Select from '@/components/forms/Select'
 * @param {object} props
 * @param {boolean} [props.loading] Shows react-select's loading indicator
 * @param {object[]} [props.options] Options as { value, label, description? }, or { label, options } groups
 * @param {*} [props.value] Selected option's scalar value, or a react-select option object passed through untouched
 * @param {number} [props.fixedWidth] Caps the control and menu at 300px; any truthy value acts as a flag, the number itself is not applied
 * @param {boolean} [props.autoWidth] Sizes the control to its content
 * @param {boolean} [props.error] Critical border and ring, matching field error styling
 * @param {boolean} [props.allowFreeText] Lets the user create options by typing
 * @param {boolean} [props.isNumberField] Restricts free text to numbers, labels entries with the units, and matches options on exact value so a typed number is never resolved to a preset containing its digits
 * @param {string} [props.units] Unit label appended to number-field entries; defaults to "minutes or more"
 * @param {boolean} [props.isClearable] Shows the clear button; defaults to true
 * @param {boolean} [props.isMulti] Delegates to the shared MultiSelect UX; ignored when allowFreeText is set
 * @param {boolean} [props.commitFreeTextOnBlur] Commits uncommitted free text on blur instead of discarding it
 */
const Select = ({
  loading,
  options = [],
  value,
  fixedWidth,
  autoWidth,
  error,
  allowFreeText,
  isNumberField,
  isClearable = true,
  isMulti,
  commitFreeTextOnBlur = false,
  ...props
}) => {
  // default units to "minutes or more"
  const units = props.units || 'minutes or more'

  // With commitFreeTextOnBlur, uncommitted free text is locked in on blur (like
  // pressing Enter) instead of being discarded. Track the current input so blur
  // can commit it; reset on any explicit selection so a stale filter string
  // can't overwrite the picked value.
  const [pendingText, setPendingText] = useState('')

  // Multi-selects share one UX, so isMulti delegates to MultiSelect rather than
  // exposing react-select's raw multi mode. Pre-delegation call sites keep the
  // react-select defaults — individual chips that wrap and grow the control —
  // so values stay uncondensed and full here; pass condenseOptions to opt in.
  if (isMulti && !allowFreeText) {
    return (
      <MultiSelect
        loading={loading}
        options={options}
        value={value}
        fixedWidth={fixedWidth}
        error={error}
        isClearable={isClearable}
        condenseOptions={false}
        showFullOptions
        {...props}
      />
    )
  }

  const freeTextBlurProps = commitFreeTextOnBlur
    ? {
        onInputChange: (input, meta) => {
          if (meta.action === 'input-change') {
            setPendingText(input)
          }
          props.onInputChange?.(input, meta)
        },
        onChange: (option, meta) => {
          setPendingText('')
          props.onChange?.(option, meta)
        },
        onBlur: (event) => {
          const trimmed = pendingText.trim()
          if (trimmed) {
            props.onChange?.({ value: trimmed, label: trimmed }, { action: 'create-option' })
          }
          setPendingText('')
          props.onBlur?.(event)
        },
      }
    : {}

  // Grouped options come in as { label, options: [...] }; flatten one level so
  // value resolution works the same whether options are flat or grouped.
  const flatOptions = options.flatMap((option) => option.options || option)

  const getOptionValue = (selectOptions, selectValue, isNumber) => {
    // Option exists
    const foundValue = selectOptions.find((option) => option.value === selectValue)
    if (foundValue) {
      return foundValue
    }
    if (!selectValue) {
      return undefined
    }
    // Option is free text
    return { value: selectValue, label: `${selectValue}${isNumber ? ` ${units}` : ''}` }
  }

  // Value is normally a scalar resolved against the options, but a react-select
  // option object is passed through untouched.
  const resolveValue = () => {
    if (isObject(value)) {
      return value
    }
    return flatOptions.find((option) => option.value === value) || ''
  }

  if (allowFreeText) {
    return (
      <CreatableSelect
        isClearable={isClearable}
        backspaceRemovesValue
        options={options}
        formatCreateLabel={
          isNumberField ? (userInput) => `Select ${userInput} ${units}` : undefined
        }
        filterOption={isNumberField ? exactNumberMatch : undefined}
        onChange={props.onChange}
        value={getOptionValue(flatOptions, value, isNumberField)}
        onKeyDown={isNumberField ? handleNumberOnlyInput : undefined}
        components={isNumberField ? { Input } : undefined}
        formatOptionLabel={formatOptionWithDescription}
        styles={selectStyles(fixedWidth, error, autoWidth)}
        theme={(theme) => selectTheme(theme)}
        menuPortalTarget={MENU_PORTAL_TARGET}
        {...props}
        {...freeTextBlurProps}
      />
    )
  }

  return (
    <ReactSelect
      isClearable={isClearable}
      isSearchable
      escapeClearsValue={false}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      options={options}
      value={resolveValue()}
      formatOptionLabel={formatOptionWithDescription}
      styles={selectStyles(fixedWidth, error, autoWidth)}
      theme={(theme) => selectTheme(theme)}
      isLoading={loading}
      menuPortalTarget={MENU_PORTAL_TARGET}
      {...props}
    />
  )
}

export default Select
